import type { ReactNode } from 'react';
import type {
  AiModerationTrace,
  ArchiveRecord,
} from '../types/moderation';
import AiSidebarSummary from './AiSidebarSummary';
import ModerationActions from './ModerationActions';
import StatusBadge from './StatusBadge';
import Callout from './ui/Callout';
import GuideExample from './GuideExample';

/**
 * G-5 — registry of inline demos for the moderator guide.
 *
 * Each entry maps a marker like `{{demo:ai-sidebar-example}}` (rendered
 * inline in `docs/pride-history/moderator-guide.md`) to a React component
 * that the GuidePage renderer swaps in at parse time. Demos are wrapped
 * in `<GuideExample>` for a consistent figure + caption frame.
 *
 * Stable, hand-curated sample data lives at the top of this file so the
 * demos render deterministically (no random ids in snapshots).
 */

const SAMPLE_TRACE: AiModerationTrace = {
  id: 'demo-trace-1',
  modelVersion: 'claude-haiku-4-5',
  invokedAt: '2026-05-10T01:23:45.000Z',
  decision: 'flagged_for_human',
  decisionReason:
    'Detected third-party PII and a possibly-related earlier submission.',
  confidence: 0.62,
  thinking:
    'Photo of a 1991 Pride march in Fortitude Valley. One placard text mentions a named individual; another submission with a similar perceptual hash exists.',
  signals: {
    safety: {
      graphicSexual: { detected: false, confidence: 0.02, rationale: 'No nudity.' },
      graphicViolence: { detected: false, confidence: 0.01, rationale: 'Peaceful march.' },
      spam: { detected: false, confidence: 0.0, rationale: 'Not spam.' },
      needsContext: { detected: false, confidence: 0.1, rationale: 'Context provided.' },
    },
    pii: {
      detected: true,
      spans: [
        {
          type: 'name',
          text: 'A named individual on a placard',
          sourceField: 'image_text',
        },
      ],
    },
    metadata: {
      themes: ['Activism', 'Community'],
      decades: ['1990s'],
      yearStart: '1991',
      yearEnd: '1991',
      altText: 'Marchers carrying placards in Fortitude Valley.',
      summary: 'Brisbane Pride march, Fortitude Valley, 1991.',
    },
    duplicate: {
      isDuplicate: true,
      similarSubmissionId: 'sub-2024-11-04-09',
      phash: '0xdeadbeef',
      hammingDistance: 4,
    },
  },
  inputs: {
    submissionId: 'demo-submission-1',
    contentType: 'image/jpeg',
    sourceKey: 'demo.jpg',
    transcriptIncluded: false,
  },
  rawApiRequestId: 'req-demo-1',
  inputTokens: 1240,
  outputTokens: 380,
  thinkingTokens: 920,
};

const SAMPLE_RECORD: ArchiveRecord = {
  submissionId: 'demo-submission-1',
  uploadId: 'demo-upload-1',
  title: 'Brisbane Pride march, Fortitude Valley',
  description: 'Marchers and placards on the Valley parade route.',
  yearStart: '1991',
  yearEnd: '1991',
  themes: ['Activism'],
  decades: ['1990s'],
  mediaType: 'photo',
  contributor: { name: 'Sample contributor' },
  consent: { timestamp: '2026-05-10T00:00:00.000Z', version: '1.0' },
  moderationStatus: 'pending_review',
  createdAt: '2026-05-10T00:00:00.000Z',
  updatedAt: '2026-05-10T00:00:00.000Z',
  pipeline: { status: 'complete', completedAt: '2026-05-10T00:01:00.000Z' },
  aiAnalysis: [SAMPLE_TRACE],
};

/**
 * Demo for the "Reading the AI moderator's verdict" section. Renders the
 * real AiSidebarSummary against a hand-curated trace (mid-confidence,
 * PII detected, possible duplicate) so moderators see what a flagged
 * submission looks like end-to-end.
 */
function AiSidebarDemo() {
  return (
    <GuideExample
      title="Example: a flagged submission with PII detected and a possible duplicate"
      description="Mid-confidence (62%), one PII span on a placard, near-match hash on file."
    >
      <AiSidebarSummary record={SAMPLE_RECORD} />
    </GuideExample>
  );
}

/**
 * Demo for the "Standard Review Flow" section — the verdict buttons as
 * they appear on every submission, wired to a noop onModerated so the
 * embedded copy stays inert.
 */
function ModerationActionsDemo() {
  return (
    <GuideExample
      title="The verdict buttons as they appear on each submission"
      description="Approve / Reject / Request Changes / Withdraw / Sensitive — disabled here so the demo can't fire an API call."
    >
      <div className="guide-example__demo-actions" aria-hidden="false">
        <ModerationActions
          record={SAMPLE_RECORD}
          onModerated={() => {
            /* demo: noop */
          }}
          disabled
        />
      </div>
    </GuideExample>
  );
}

interface StatusRow {
  status: string;
  description: string;
}

const STATUS_ROWS: StatusRow[] = [
  {
    status: 'pending_review',
    description: 'New submission awaiting first-look from a moderator.',
  },
  {
    status: 'approved',
    description: 'Cleared for publication; will mirror to WordPress.',
  },
  {
    status: 'rejected',
    description: 'Declined for publication; the contributor is notified.',
  },
  {
    status: 'awaiting_contributor',
    description:
      'Sent back to the contributor with a Request Changes message; parked until they reply.',
  },
  {
    status: 'withdrawn',
    description:
      'Removed at the contributor’s request or because of post-publication concerns.',
  },
];

/**
 * Demo for the "Decision Standards" section. Custom table layout (not
 * a single primitive), so it's implemented here as a self-contained
 * component rather than wrapped around a workbench primitive.
 */
function StatusBadgesTableDemo() {
  return (
    <GuideExample
      title="Status badge legend"
      description="Every submission carries one of these statuses through the moderation pipeline."
    >
      <table className="guide-example__status-table">
        <thead>
          <tr>
            <th scope="col">Status</th>
            <th scope="col">When this applies</th>
          </tr>
        </thead>
        <tbody>
          {STATUS_ROWS.map((row) => (
            <tr key={row.status}>
              <td>
                <StatusBadge status={row.status} />
              </td>
              <td>{row.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </GuideExample>
  );
}

/**
 * Demo for the "Sensitivity And Privacy" section. Renders the warning-tone
 * Callout with copy about handling sensitive content. Mirrors what a
 * moderator sees as an inline reminder when the AI flags a PII span.
 */
function SensitivityCalloutDemo() {
  return (
    <GuideExample
      title="Sensitivity reminder — what the warning callout looks like"
      description="Use this when escalating, deferring, or annotating risk on a submission."
    >
      <Callout variant="warning">
        Sensitive material — names, health information, minors, or
        identity-safety risks — must be reviewed against the contributor’s
        consent, the AI’s PII findings, and the project’s privacy
        guidelines before approving. Add a sensitivity note explaining the
        concern; escalate if publication may create legal, privacy, safety,
        cultural, or reputational risk.
      </Callout>
    </GuideExample>
  );
}

/**
 * Registry of marker → component. The GuidePage renderer matches
 * `{{demo:<id>}}` on its own line and replaces it with the corresponding
 * component. Keep keys kebab-case and stable; they're referenced from
 * markdown content.
 */
export const GUIDE_DEMOS: Record<string, () => ReactNode> = {
  'ai-sidebar-example': AiSidebarDemo,
  'moderation-actions-example': ModerationActionsDemo,
  'status-badges-table': StatusBadgesTableDemo,
  'sensitivity-callout': SensitivityCalloutDemo,
};

/**
 * Test/registry helper: returns the rendered demo for a marker id, or
 * null if the id is unknown.
 */
export function renderGuideDemo(id: string): ReactNode {
  const factory = GUIDE_DEMOS[id];
  return factory ? factory() : null;
}
